import { useEffect, useState, useCallback, useRef } from "react";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { useSession, useUser } from "@clerk/clerk-react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { CandidateFollowUpActionType, type FollowUpSummary } from "shared/types/candidate-follow-up";
import * as Sentry from "@sentry/react";
import { useUsers } from "shared/contexts/users.context";
import type {
  CandidateInfo,
  UnitInfoEntry,
  Filters,
  ResidentDocumentAsset,
} from "../types";
import { transformCandidateResponse } from "../types";

const PAGE_SIZE = 50;

export type KpiFilter = "interested" | "notFollowedUp" | "stale" | "awaitingInterested" | "notInterested" | null;

interface UseCandidateTableDataParams {
  buildingId?: string;
  filters?: Filters;
  includeArchived?: boolean;
  onCandidateClick?: (candidate: CandidateInfo) => void;
}

export function useCandidateTableData({
  buildingId,
  filters,
  includeArchived = false,
  onCandidateClick,
}: UseCandidateTableDataParams) {
  const { session } = useSession();
  const { user } = useUser();
  const navigate = useNavigate();
  const location = useLocation();
  const { users: clerkUsers } = useUsers();

  // --- Core state ---
  const [candidates, setCandidates] = useState<CandidateInfo[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [isProcessing, setIsProcessing] = useState(false);

  // --- KPI ---
  const [kpiCounts, setKpiCounts] = useState({
    interestedCount: 0,
    notFollowedUpCount: 0,
    staleCount: 0,
    awaitingInterestedCount: 0,
    notInterestedCount: 0,
  });
  const [activeKpiFilter, setActiveKpiFilter] = useState<KpiFilter>(null);

  // --- Selection ---
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // --- Unit info ---
  const [unitInfo, setUnitInfo] = useState<Map<string, UnitInfoEntry>>(new Map());

  // --- Follow-up ---
  const [followUpSummaries, setFollowUpSummaries] = useState<Record<string, FollowUpSummary>>({});
  const followUpIsDirty = useRef(false);

  // --- Email schedules ---
  const [emailSchedules, setEmailSchedules] = useState<Record<string, import("../types").CandidateEmailScheduleEntry[]>>({});

  // --- Modal state ---
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState<CandidateInfo | null>(null);
  const [showBulkEmailModal, setShowBulkEmailModal] = useState(false);
  const [isCandidateDetailModalOpen, setIsCandidateDetailModalOpen] = useState(false);
  const [candidateDetailInfo, setCandidateDetailInfo] = useState<CandidateInfo | null>(null);
  const [isDateSelectionModalOpen, setIsDateSelectionModalOpen] = useState(false);
  const [selectedCandidateForDateSelection, setSelectedCandidateForDateSelection] = useState<CandidateInfo | null>(null);
  const [isSelectingDate, setIsSelectingDate] = useState(false);
  const [isManualAppointmentModalOpen, setIsManualAppointmentModalOpen] = useState(false);
  const [selectedCandidateForManualAppointment, setSelectedCandidateForManualAppointment] = useState<CandidateInfo | null>(null);
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [selectedCandidateForArchive, setSelectedCandidateForArchive] = useState<CandidateInfo | null>(null);
  const [documentPreview, setDocumentPreview] = useState<ResidentDocumentAsset[]>([]);
  const [documentPreviewTitle, setDocumentPreviewTitle] = useState("");
  const [isDocumentPreviewOpen, setIsDocumentPreviewOpen] = useState(false);
  const [isDocumentPreviewLoading, setIsDocumentPreviewLoading] = useState(false);
  const [expandedDocument, setExpandedDocument] = useState<(ResidentDocumentAsset & { index: number }) | null>(null);
  const [isFollowUpModalOpen, setIsFollowUpModalOpen] = useState(false);
  const [selectedCandidateForFollowUp, setSelectedCandidateForFollowUp] = useState<CandidateInfo | null>(null);

  // ========== HELPERS ==========

  // Source of truth for entity type is the backend `isInvited` flag. Derived
  // statuses "reminder-sent" and "forwarded-to-broker" exist in BOTH the Invited
  // and Application tables, so classifying by status alone misroutes archive
  // calls (an Invited row would hit archive-application → "Application not found").
  // Fall back to status only for legacy/cached rows that predate the flag.
  const isInvitedCandidate = (c: CandidateInfo) => {
    if (typeof c.isInvited === "boolean") return c.isInvited;
    return c.status === "notify" || c.status === "invited" || c.status === "reminder-sent";
  };

  const isApplicationCandidate = (c: CandidateInfo) => !isInvitedCandidate(c);

  // ========== DATA FETCHING ==========

  const fetchFollowUpSummariesForPage = useCallback(
    async (candidateList: CandidateInfo[]) => {
      if (!session || candidateList.length === 0) return;

      const applicationIds = candidateList
        .filter(isApplicationCandidate)
        .map((c) => Number(c.id))
        .filter((id) => !isNaN(id));

      if (applicationIds.length === 0) return;

      try {
        const response = await fetchWithAuth(
          `${API_URL}/rental-appointment/follow-up/summaries`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ applicationIds }),
          },
          session,
        );

        if (response.ok) {
          const summaries = await response.json();
          const map: Record<string, FollowUpSummary> = {};
          for (const s of summaries) {
            map[String(s.applicationId)] = s;
          }
          setFollowUpSummaries(map);
        } else {
          Sentry.captureException(
            new Error("Failed to fetch follow-up summaries"),
            {
              extra: { status: response.status, applicationIds },
              tags: { feature: "candidateFollowUp", method: "fetchFollowUpSummariesForPage" },
            },
          );
        }
      } catch (error) {
        console.error("Error fetching follow-up summaries:", error);
        Sentry.captureException(error, {
          tags: { feature: "candidateFollowUp", method: "fetchFollowUpSummariesForPage" },
          extra: { applicationIds },
        });
      }
    },
    [session],
  );

  const fetchEmailSchedulesForPage = useCallback(
    async (candidateList: CandidateInfo[]) => {
      if (!session || candidateList.length === 0) return;

      const applicationIds = candidateList
        .filter(isApplicationCandidate)
        .map((c) => Number(c.id))
        .filter((id) => !isNaN(id));

      if (applicationIds.length === 0) return;

      try {
        const response = await fetchWithAuth(
          `${API_URL}/candidate-email-schedule/batch`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ applicationIds }),
          },
          session,
        );

        if (response.ok) {
          const results: { applicationId: number; schedules: import("../types").CandidateEmailScheduleEntry[] }[] = await response.json();
          const map: Record<string, import("../types").CandidateEmailScheduleEntry[]> = {};
          for (const entry of results) {
            map[String(entry.applicationId)] = entry.schedules;
          }
          setEmailSchedules(map);
        }
      } catch (error) {
        console.error("Error fetching email schedules:", error);
      }
    },
    [session],
  );

  const fetchCandidates = useCallback(async () => {
    if (!session) return;
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("page", String(page));
      params.set("pageSize", String(PAGE_SIZE));
      params.set("sortField", "createDate");
      params.set("sortDirection", "DESC");
      if (buildingId) params.set("buildingId", buildingId);
      if (includeArchived) params.set("includeArchived", "true");
      if (filters?.unit) params.set("unitId", filters.unit);
      if (!buildingId && filters?.building) params.set("buildingId", filters.building);
      if (filters?.excludeRentedUnits) params.set("excludeRentedUnits", "true");

      if (activeKpiFilter === "interested") {
        params.set("status", "interested");
      } else if (activeKpiFilter === "notFollowedUp") {
        params.set("notFollowedUp", "true");
      } else if (activeKpiFilter === "stale") {
        params.set("stale", "true");
      } else if (activeKpiFilter === "awaitingInterested") {
        params.set("status", "awaiting-interested");
      } else if (activeKpiFilter === "notInterested") {
        params.set("status", "not-interested");
      } else {
        if (filters?.statusArray?.length) params.set("status", filters.statusArray.join(","));
        else if (filters?.status) params.set("status", filters.status);
      }
      if (filters?.searchText?.trim()) params.set("search", filters.searchText.trim());

      const response = await fetchWithAuth(
        `${API_URL}/rental-appointment/get-appointments-paginated?${params.toString()}`,
        { method: "GET" },
        session,
      );

      if (!response.ok) throw new Error(`API error: ${response.status}`);

      const result = await response.json();
      const transformed = transformCandidateResponse(result.data);
      setCandidates(transformed);
      setTotalCount(result.totalCount);
      fetchFollowUpSummariesForPage(transformed);
      fetchEmailSchedulesForPage(transformed);
    } catch (error) {
      console.error("Error fetching candidates:", error);
      Sentry.captureException(error, {
        tags: { feature: "candidateTable", method: "fetchCandidates" },
        extra: { buildingId, page, includeArchived, activeKpiFilter },
      });
      setCandidates([]);
      setTotalCount(0);
    } finally {
      setIsLoading(false);
    }
  }, [session, page, buildingId, includeArchived, filters, activeKpiFilter, fetchFollowUpSummariesForPage, fetchEmailSchedulesForPage]);

  const fetchKpiCounts = useCallback(async () => {
    if (!session) return;
    try {
      const params = new URLSearchParams();
      if (buildingId) params.set("buildingId", buildingId);
      if (!buildingId && filters?.building) params.set("buildingId", filters.building);
      if (filters?.unit) params.set("unitId", filters.unit);
      if (filters?.searchText) params.set("searchText", filters.searchText);
      if (includeArchived) params.set("includeArchived", "true");
      // Must mirror the list query — otherwise the chips count candidates the list hides.
      if (filters?.excludeRentedUnits) params.set("excludeRentedUnits", "true");

      const response = await fetchWithAuth(
        `${API_URL}/rental-appointment/follow-up/kpi-counts?${params.toString()}`,
        { method: "GET" },
        session,
      );
      if (response.ok) {
        setKpiCounts(await response.json());
      } else {
        Sentry.captureException(
          new Error("Failed to fetch KPI counts"),
          {
            extra: { status: response.status },
            tags: { feature: "candidateKpi", method: "fetchKpiCounts" },
          },
        );
      }
    } catch (error) {
      console.error("Error fetching KPI counts:", error);
      Sentry.captureException(error, { tags: { feature: "candidateKpi", method: "fetchKpiCounts" } });
    }
  }, [session, buildingId, includeArchived, filters?.unit, filters?.building, filters?.searchText, filters?.excludeRentedUnits]);

  // ========== EFFECTS ==========

  // Reset page on filter change
  useEffect(() => {
    setPage(1);
  }, [buildingId, includeArchived, filters?.unit, filters?.building, filters?.status, filters?.searchText, filters?.excludeRentedUnits, JSON.stringify(filters?.statusArray)]);

  // Fetch candidates
  useEffect(() => {
    fetchCandidates();
  }, [fetchCandidates]);

  // Fetch KPI counts
  useEffect(() => {
    fetchKpiCounts();
  }, [fetchKpiCounts]);

  // Fetch vacancy statuses
  useEffect(() => {
    if (!session) return;
    const fetchVacancyStatuses = async () => {
      try {
        const [vacantRes, upcomingRes, renovationRes, conceptRes] = await Promise.all([
          fetchWithAuth(`${API_URL}/rental/metrics/enhanced-vacant-units`, { method: "GET" }, session),
          fetchWithAuth(`${API_URL}/rental/metrics/enhanced-upcoming-vacant-units`, { method: "GET" }, session),
          fetchWithAuth(`${API_URL}/rental/units/in-renovation`, { method: "GET" }, session),
          fetchWithAuth(`${API_URL}/units/almost-rented`, { method: "GET" }, session),
        ]);

        const infoMap = new Map<string, UnitInfoEntry>();

        const setUnit = (u: any, status: UnitInfoEntry["status"]) => {
          const contact = u.visitationResponsibleContact ?? null;
          let enrichedContact = contact;
          if (contact) {
            const byEmail = contact.email
              ? clerkUsers.find((cu: any) =>
                  cu.email_addresses?.some(
                    (ea: any) => ea.email_address?.toLowerCase() === contact.email.toLowerCase()
                  )
                )
              : null;
            const byName =
              !byEmail && contact.firstName && contact.lastName
                ? clerkUsers.find(
                    (cu: any) =>
                      cu.first_name?.toLowerCase() === contact.firstName.toLowerCase() &&
                      cu.last_name?.toLowerCase() === contact.lastName.toLowerCase()
                  )
                : null;
            const matched = byEmail ?? byName ?? null;
            if (matched) {
              enrichedContact = { ...contact, picture: matched.image_url ?? null };
            }
          }
          infoMap.set(u.id, {
            status,
            availableTimeslots: u.availableTimeslots ?? 0,
            timeslotDetails: u.timeslotDetails ?? [],
            visitationContact: enrichedContact,
            brokerContacts: Array.isArray(u.brokerContacts) ? u.brokerContacts : [],
            buildingAddress: u.building?.googlePlaceLongAddress ?? null,
          });
        };

        if (renovationRes.ok) for (const u of await renovationRes.json()) setUnit(u, "renovation");
        if (upcomingRes.ok) for (const u of await upcomingRes.json()) setUnit(u, "upcoming");
        if (conceptRes.ok) for (const u of await conceptRes.json()) setUnit(u, "concept");
        if (vacantRes.ok) for (const u of await vacantRes.json()) setUnit(u, "vacant");

        setUnitInfo(infoMap);
      } catch (error) {
        console.error("Error fetching vacancy statuses:", error);
        Sentry.captureException(error, {
          tags: { feature: "candidateTable", method: "fetchVacancyStatuses" },
        });
      }
    };
    fetchVacancyStatuses();
  }, [session, clerkUsers]);

  // Clear selection on page/filter change
  useEffect(() => {
    setSelectedIds(new Set());
  }, [page, activeKpiFilter, filters]);

  // Open candidate detail from navigation state
  useEffect(() => {
    const application = location.state?.application;
    if (application) {
      handleOpenCandidateDetailModal(application);
      window.history.replaceState({}, "");
    }
  }, []);

  // Refresh candidates when date selection modal opens
  useEffect(() => {
    if (isDateSelectionModalOpen && selectedCandidateForDateSelection) {
      fetchCandidates();
    }
  }, [isDateSelectionModalOpen, selectedCandidateForDateSelection]);

  // ========== HANDLERS ==========

  const refreshFollowUpSummaries = useCallback(() => {
    fetchFollowUpSummariesForPage(candidates);
  }, [fetchFollowUpSummariesForPage, candidates]);

  const handleKpiFilterClick = useCallback(
    (filter: "interested" | "notFollowedUp" | "stale" | "awaitingInterested" | "notInterested") => {
      setActiveKpiFilter((prev) => (prev === filter ? null : filter));
      setPage(1);
    },
    [],
  );

  const handleToggleSelect = useCallback((candidateId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(candidateId)) {
        next.delete(candidateId);
      } else {
        next.add(candidateId);
      }
      return next;
    });
  }, []);

  const handleOpenCandidateDetailModal = useCallback(
    (candidateInfo: CandidateInfo) => {
      if (onCandidateClick) {
        onCandidateClick(candidateInfo);
      } else {
        setCandidateDetailInfo(candidateInfo);
        setIsCandidateDetailModalOpen(true);
      }
    },
    [onCandidateClick],
  );

  const handleCloseCandidateDetailModal = useCallback(
    () => setIsCandidateDetailModalOpen(false),
    [],
  );

  const handleOpenDateSelectionModal = useCallback((candidate: CandidateInfo) => {
    setSelectedCandidateForDateSelection(candidate);
    setIsDateSelectionModalOpen(true);
  }, []);

  const handleCloseDateSelectionModal = useCallback(() => {
    setIsDateSelectionModalOpen(false);
    setSelectedCandidateForDateSelection(null);
  }, []);

  const handleOpenManualAppointmentModal = useCallback((candidate: CandidateInfo) => {
    setSelectedCandidateForManualAppointment(candidate);
    setIsManualAppointmentModalOpen(true);
  }, []);

  const handleCloseManualAppointmentModal = useCallback(() => {
    setIsManualAppointmentModalOpen(false);
    setSelectedCandidateForManualAppointment(null);
  }, []);

  const handleOpenDocumentPreview = useCallback(
    async (residentId: string, imageTypes: string[], title: string) => {
      if (!residentId) return;
      if (!session) {
        toast.error("Sessie is verlopen. Ververs de pagina en probeer opnieuw.");
        return;
      }

      setIsDocumentPreviewOpen(true);
      setDocumentPreviewTitle(title || "Documenten");
      setDocumentPreview([]);
      setIsDocumentPreviewLoading(true);

      try {
        const response = await fetchWithAuth(
          `${API_URL}/rental-appointment/get-resident-image/${residentId}?imageType=${imageTypes.join(",")}`,
          { method: "GET" },
          session,
        );

        if (response.ok) {
          const images = await response.json();
          const normalizedImages = Array.isArray(images) ? images : [];
          setDocumentPreview(normalizedImages);
          if (normalizedImages.length === 0) {
            toast.info("Geen documenten gevonden voor deze kandidaat.");
          }
        } else {
          toast.error("Document kon niet geladen worden");
        }
      } catch (error) {
        console.error("Error fetching resident documents:", error);
        toast.error("Document kon niet geladen worden");
        Sentry.captureException(error, {
          tags: { feature: "candidateTable", method: "handleOpenDocumentPreview" },
          extra: { residentId },
        });
      } finally {
        setIsDocumentPreviewLoading(false);
      }
    },
    [session],
  );

  const handleCloseDocumentPreview = useCallback(() => {
    setIsDocumentPreviewOpen(false);
    setDocumentPreview([]);
    setDocumentPreviewTitle("");
    setIsDocumentPreviewLoading(false);
    setExpandedDocument(null);
  }, []);

  const handleExpandDocument = useCallback(
    (doc: ResidentDocumentAsset, index: number) => {
      setExpandedDocument({ ...doc, index });
    },
    [],
  );

  const handleCloseExpandedDocument = useCallback(() => {
    setExpandedDocument(null);
  }, []);

  const handleFollowUpClick = useCallback((candidate: CandidateInfo) => {
    followUpIsDirty.current = false;
    setSelectedCandidateForFollowUp(candidate);
    setIsFollowUpModalOpen(true);
  }, []);

  const handleFollowUpModalClose = useCallback(() => {
    setIsFollowUpModalOpen(false);
    setSelectedCandidateForFollowUp(null);
    if (followUpIsDirty.current) {
      followUpIsDirty.current = false;
      refreshFollowUpSummaries();
    }
  }, [refreshFollowUpSummaries]);

  const handleFollowUpDataChanged = useCallback(() => {
    followUpIsDirty.current = true;
  }, []);

  const handleLogPhoneCall = useCallback(
    async (candidate: CandidateInfo, phoneNumber: string, durationSeconds?: number) => {
      if (!session) return;
      if (!isApplicationCandidate(candidate)) return;

      try {
        const response = await fetchWithAuth(
          `${API_URL}/rental-appointment/follow-up/actions`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              applicationId: Number(candidate.id),
              actionType: durationSeconds && durationSeconds > 0
                ? CandidateFollowUpActionType.PHONE_REACHED
                : CandidateFollowUpActionType.PHONE_UNREACHABLE,
              remarks: `Telefoongesprek ${phoneNumber}${durationSeconds ? ` (${Math.floor(durationSeconds / 60)}:${String(durationSeconds % 60).padStart(2, "0")})` : ""}`,
              createdByUserId: user?.id,
            }),
          },
          session,
        );

        if (response.ok) {
          toast.success("Telefoongesprek gelogd als opvolgingsactie");
          refreshFollowUpSummaries();
        } else {
          toast.error("Fout bij het loggen van telefoongesprek");
          Sentry.captureException(
            new Error("Failed to log phone call as follow-up action"),
            {
              extra: { applicationId: candidate.id, phoneNumber, status: response.status },
              tags: { feature: "candidateFollowUp", method: "handleLogPhoneCall" },
            },
          );
        }
      } catch (error) {
        console.error("Error logging phone call as follow-up action:", error);
        toast.error("Fout bij het loggen van telefoongesprek");
        Sentry.captureException(error, {
          tags: { feature: "candidateFollowUp", method: "handleLogPhoneCall" },
          extra: { applicationId: candidate.id, phoneNumber },
        });
      }
    },
    [session, user, refreshFollowUpSummaries],
  );

  const handleDateSelectionConfirm = async (selectedDate: Date) => {
    if (!selectedCandidateForDateSelection?.id) return;
    if (!session) {
      toast.error("Sessie is verlopen. Ververs de pagina en probeer opnieuw.");
      return;
    }

    setIsSelectingDate(true);
    try {
      const response = await fetchWithAuth(
        `${API_URL}/rental-appointment/select-alternate-date/${selectedCandidateForDateSelection.id}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ selectedDate: selectedDate.toISOString() }),
        },
        session,
      );

      if (response.ok) {
        toast.success("Datum succesvol geselecteerd!");
        setIsDateSelectionModalOpen(false);
        setSelectedCandidateForDateSelection(null);
        fetchCandidates();
      } else {
        toast.error("Fout bij het selecteren van de datum");
      }
    } catch (error) {
      console.error("Error selecting date:", error);
      if (error instanceof Error && error.message.includes("not found")) {
        toast.error("De kandidaat bestaat niet meer. De pagina wordt ververst...");
        setTimeout(() => window.location.reload(), 2000);
      } else {
        toast.error("Fout bij het selecteren van de datum");
      }
    } finally {
      setIsSelectingDate(false);
    }
  };

  const handleDisapproveAlternates = async (candidate: CandidateInfo) => {
    if (!candidate.id) return;
    if (!session) {
      toast.error("Sessie is verlopen. Ververs de pagina en probeer opnieuw.");
      return;
    }

    setIsProcessing(true);
    try {
      const response = await fetchWithAuth(
        `${API_URL}/rental-appointment/disapprove-alternate-dates/${candidate.id}`,
        { method: "PUT", headers: { "Content-Type": "application/json" } },
        session,
      );

      if (response.ok) {
        const result = await response.json();
        const personalDetails = result.personalDetails;
        const building = result.building;

        toast.success("Alternatieve data afgekeurd. U wordt doorgestuurd naar de e-mail pagina.");

        const buildingAddress = building?.googlePlaceLongAddress || "gebouw";
        const emailSubject = `Uw bezoek aan ${buildingAddress} - Voorgestelde alternatieve data helaas niet mogelijk`;

        navigate("/mass-mail", {
          state: {
            contacts: [
              {
                id: candidate.id,
                name: `${personalDetails?.firstName || ""} ${personalDetails?.lastName || ""}`.trim(),
                email: personalDetails?.email || "",
                firstName: personalDetails?.firstName || "",
                lastName: personalDetails?.lastName || "",
                resourceId: candidate.unitId || "",
                units: [],
              },
            ],
            emailSubject,
            emailContent: `Beste ${personalDetails?.firstName || ""},

Helaas zijn geen van de door u voorgestelde data voor een bezichtiging van ${buildingAddress} voor ons mogelijk.

**Optie 1:** Kan u eventueel een bezoek inplannen op één van volgende data:
[Voeg hier beschikbare data toe]

**Optie 2:** Een medewerker van Yally zal u telefonisch contacteren om een beter moment te zoeken.

**Optie 3:** U kan ook zelf een nieuwe datum boeken via https://yally.apartments

Met vriendelijke groet,
Team Yally`,
          },
        });

        fetchCandidates();
      } else {
        const errorText = await response.text();
        console.error("Error disapproving alternates:", errorText);
        toast.error("Fout bij het afkeuren van alternatieve data");
      }
    } catch (error) {
      console.error("Error disapproving alternates:", error);
      toast.error("Fout bij het afkeuren van alternatieve data");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleManualAppointmentConfirm = async (visitDate: Date) => {
    if (!selectedCandidateForManualAppointment?.id) return;
    if (!session) {
      toast.error("Sessie is verlopen. Ververs de pagina en probeer opnieuw.");
      return;
    }

    setIsProcessing(true);
    try {
      const response = await fetchWithAuth(
        `${API_URL}/rental-appointment/set-manual-appointment/${selectedCandidateForManualAppointment.id}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ visitDate: visitDate.toISOString() }),
        },
        session,
      );

      if (response.ok) {
        toast.success("Afspraak succesvol ingepland");
        handleCloseManualAppointmentModal();
        fetchCandidates();
      } else {
        const errorText = await response.text();
        console.error("Error setting manual appointment:", errorText);
        toast.error("Fout bij het inplannen van afspraak");
      }
    } catch (error) {
      console.error("Error setting manual appointment:", error);
      toast.error("Fout bij het inplannen van afspraak");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleEmailSuccess = useCallback(() => {
    setShowEmailModal(false);
    setSelectedCandidate(null);
    setSelectedIds(new Set());
    fetchCandidates();
    fetchKpiCounts();
  }, [fetchCandidates, fetchKpiCounts]);

  const handleOpenArchiveModal = (candidate: CandidateInfo) => {
    setSelectedCandidateForArchive(candidate);
    setShowArchiveModal(true);
  };

  const handleArchiveConfirm = async (reason: string) => {
    if (!selectedCandidateForArchive) return;
    setIsProcessing(true);

    try {
      const isInvited = isInvitedCandidate(selectedCandidateForArchive);

      if (isInvited) {
        const response = await fetchWithAuth(
          `${API_URL}/rental-appointment/delete-invited/${selectedCandidateForArchive.id}`,
          { method: "DELETE" },
          session,
        );
        if (response.ok) {
          toast.success("Kandidaat succesvol verwijderd!");
          setShowArchiveModal(false);
          setSelectedCandidateForArchive(null);
          fetchCandidates();
        } else {
          toast.error("Fout bij verwijderen kandidaat");
        }
      } else {
        const response = await fetchWithAuth(
          `${API_URL}/rental-appointment/archive-application/${selectedCandidateForArchive.id}`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ reason }),
          },
          session,
        );
        if (response.ok) {
          toast.success("Kandidaat succesvol gearchiveerd!");
          setShowArchiveModal(false);
          setSelectedCandidateForArchive(null);
          fetchCandidates();
        } else {
          toast.error("Fout bij archiveren kandidaat");
        }
      }
    } catch (error) {
      console.error("Error archiving/deleting candidate:", error);
      toast.error(
        isInvitedCandidate(selectedCandidateForArchive)
          ? "Fout bij verwijderen kandidaat"
          : "Fout bij archiveren kandidaat",
      );
      Sentry.captureException(error, {
        tags: { feature: "candidateTable", method: "handleArchiveConfirm" },
        extra: { candidateId: selectedCandidateForArchive.id },
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleFollowUpChange = useCallback(
    async (candidate: CandidateInfo, newStatus: "none" | "ok" | "problem", remark?: string) => {
      if (!session) {
        toast.error("Sessie is verlopen. Ververs de pagina en probeer opnieuw.");
        return;
      }

      try {
        const response = await fetchWithAuth(
          `${API_URL}/rental-appointment/update-follow-up/${candidate.id}`,
          {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ followUpStatus: newStatus, followUpRemark: remark }),
          },
          session,
        );

        if (response.ok) {
          setCandidates((prev) =>
            prev.map((c) =>
              c.id === candidate.id
                ? { ...c, followUpStatus: newStatus, followUpRemark: newStatus === "problem" ? (remark || null) : null }
                : c,
            ),
          );
        } else {
          toast.error("Fout bij het bijwerken van opvolgingsstatus");
          Sentry.captureException(
            new Error("Failed to update follow-up status"),
            {
              extra: { candidateId: candidate.id, newStatus, status: response.status },
              tags: { feature: "candidateFollowUp", method: "handleFollowUpChange" },
            },
          );
        }
      } catch (error) {
        console.error("Error updating follow-up status:", error);
        toast.error("Fout bij het bijwerken van opvolgingsstatus");
        Sentry.captureException(error, {
          tags: { feature: "candidateFollowUp", method: "handleFollowUpChange" },
          extra: { candidateId: candidate.id, newStatus },
        });
      }
    },
    [session],
  );

  const handleBulkArchive = useCallback(async () => {
    if (selectedIds.size === 0 || !session) return;

    const confirmed = window.confirm(
      `Weet u zeker dat u ${selectedIds.size} kandidaten wilt archiveren?`,
    );
    if (!confirmed) return;

    const applicationIds: number[] = [];
    const invitedIds: number[] = [];

    for (const id of selectedIds) {
      const candidate = candidates.find((c) => c.id === id);
      if (candidate) {
        if (isInvitedCandidate(candidate)) {
          invitedIds.push(Number(id));
        } else {
          applicationIds.push(Number(id));
        }
      }
    }

    try {
      const response = await fetchWithAuth(
        `${API_URL}/rental-appointment/bulk-archive`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ applicationIds, invitedIds, reason: "Bulk gearchiveerd" }),
        },
        session,
      );

      if (response.ok) {
        const result = await response.json();
        toast.success(
          `${(result.archivedCount || 0) + (result.deletedCount || 0)} kandidaten gearchiveerd`,
        );
        setSelectedIds(new Set());
        fetchCandidates();
        fetchKpiCounts();
      } else {
        toast.error("Fout bij bulk archiveren");
        Sentry.captureException(
          new Error("Bulk archive request failed"),
          {
            extra: { status: response.status, applicationIds, invitedIds },
            tags: { feature: "bulkArchive", method: "handleBulkArchive" },
          },
        );
      }
    } catch (error) {
      console.error("Error bulk archiving:", error);
      toast.error("Fout bij bulk archiveren");
      Sentry.captureException(error, {
        tags: { feature: "bulkArchive", method: "handleBulkArchive" },
        extra: { applicationIds, invitedIds },
      });
    }
  }, [selectedIds, candidates, session, fetchCandidates, fetchKpiCounts]);

  const handleBulkRejectWithEmail = useCallback(() => {
    if (selectedIds.size === 0) return;
    setShowBulkEmailModal(true);
  }, [selectedIds]);

  const handleCloseEmailModal = () => {
    setShowEmailModal(false);
    setSelectedCandidate(null);
    setIsProcessing(false);
  };

  const handleManageCandidate = useCallback((candidate: CandidateInfo) => {
    setSelectedCandidate(candidate);
    setShowEmailModal(true);
  }, []);

  // ========== ENRICHED CANDIDATES ==========

  const enrichedCandidates = candidates.map((c) => ({
    ...c,
    followUpSummary: followUpSummaries[c.id] ?? c.followUpSummary ?? null,
    emailSchedules: emailSchedules[c.id] ?? c.emailSchedules ?? [],
  }));

  return {
    // Data
    candidates: enrichedCandidates,
    totalCount,
    isLoading,
    isProcessing,
    page,
    setPage,
    pageSize: PAGE_SIZE,
    unitInfo,

    // KPI
    kpiCounts,
    activeKpiFilter,
    handleKpiFilterClick,

    // Selection
    selectedIds,
    setSelectedIds,
    handleToggleSelect,

    // Bulk actions
    handleBulkArchive,
    handleBulkRejectWithEmail,

    // Handlers for cell factory
    handleOpenCandidateDetailModal,
    handleManageCandidate,
    handleOpenDocumentPreview,
    handleOpenDateSelectionModal,
    handleDisapproveAlternates,
    handleOpenManualAppointmentModal,
    handleOpenArchiveModal,
    handleFollowUpClick,
    handleLogPhoneCall,

    // Modal state + handlers
    showEmailModal,
    selectedCandidate,
    handleCloseEmailModal,
    handleEmailSuccess,
    showBulkEmailModal,
    setShowBulkEmailModal,
    isCandidateDetailModalOpen,
    candidateDetailInfo,
    handleCloseCandidateDetailModal,
    handleFollowUpChange,
    setCandidateDetailInfo,
    isDateSelectionModalOpen,
    selectedCandidateForDateSelection,
    handleCloseDateSelectionModal,
    handleDateSelectionConfirm,
    isSelectingDate,
    isManualAppointmentModalOpen,
    selectedCandidateForManualAppointment,
    handleCloseManualAppointmentModal,
    handleManualAppointmentConfirm,
    showArchiveModal,
    setShowArchiveModal,
    selectedCandidateForArchive,
    setSelectedCandidateForArchive,
    handleArchiveConfirm,
    isDocumentPreviewOpen,
    documentPreview,
    documentPreviewTitle,
    isDocumentPreviewLoading,
    handleCloseDocumentPreview,
    expandedDocument,
    handleExpandDocument,
    handleCloseExpandedDocument,
    isFollowUpModalOpen,
    selectedCandidateForFollowUp,
    handleFollowUpModalClose,
    handleFollowUpDataChanged,
    followUpSummaries,

    // For refreshing
    fetchCandidates,
  };
}
